
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router';
import { SEO } from './SEO';
import { StructuredData, getBreadcrumbSchema } from './StructuredData';

const services = [
  {
    id: 'safari',
    title: 'Airport Transfers Johannesburg',
    description: 'Travel comfortably between OR Tambo International Airport and Johannesburg, Pretoria and surrounding destinations with reliable private airport transfer services.',
    image: '/airport-graded.jpg',
    alt: 'Private airport transfers at OR Tambo International Airport Johannesburg',
  },
  {
    id: 'transfer',
    title: 'Private & Corporate Transport',
    description: 'Professional private and corporate transportation for business travel, meetings, events, executive movements and important guests across Johannesburg and South Africa.',
    image: '/premium-fleet-graded.jpg',
    alt: 'Executive private and corporate vehicle transport in Johannesburg',
  },
  {
    id: 'city',
    title: 'Tours & Tourism Experiences',
    description: 'Explore Johannesburg and South Africa with comfortable private tours and tourism experiences designed for individuals, families and visiting groups.',
    image: '/guided-graded.jpg',
    alt: 'Guided private tours and tourism experiences across South Africa',
  },
  {
    id: 'shuttle',
    title: 'Shuttle & Group Transportation',
    description: 'Reliable group transportation and shuttle services for corporate teams, events, delegations, conferences, tours and private groups.',
    image: '/shuttle-graded.jpg',
    alt: 'Group shuttle and corporate event transportation in Johannesburg',
  }
];

export function Services() {
  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <SEO 
        title="Transport Services Johannesburg & South Africa | Skiva Africa Tours"
        description="Explore transport services from Skiva Africa Tours, including Johannesburg airport transfers, private and corporate transport, shuttle services, group transportation and South Africa tours."
        canonical="https://skivatours.com/services"
      />
      <StructuredData 
        data={getBreadcrumbSchema([
          { name: 'Home', url: 'https://skivatours.com/' },
          { name: 'Services', url: 'https://skivatours.com/services' }
        ])} 
      />
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-20"
      >
        <h2 className="text-gold-base text-sm font-bold tracking-[0.3em] uppercase mb-4">Our Transport &amp; Tourism Services</h2>
        <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-wider">Transport Services in Johannesburg &amp; South Africa</h1>
        <p className="text-white/70 font-light leading-relaxed max-w-3xl mx-auto mt-6">
          Skiva Africa Tours provides professional transport services in Johannesburg and across South Africa, with reliable solutions for airport transfers, private travel, corporate transportation, group travel, shuttle services and tourism experiences.
        </p>
        <div className="w-24 h-1 bg-gold-base mx-auto mt-8"></div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="group relative overflow-hidden rounded-sm bg-[#1a1a1a]"
          >
            <div className="aspect-[4/3] w-full overflow-hidden relative">
              <img 
                src={service.image} 
                alt={service.alt} 
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
              <h3 className="text-2xl font-bold text-white uppercase tracking-wider mb-3">{service.title}</h3>
              <p className="text-white/70 font-light leading-relaxed mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                {service.description}
              </p>
              <Link 
                to={`/book?service=${service.id}`}
                className="inline-flex items-center text-gold-base font-bold text-xs uppercase tracking-widest hover:text-white transition-colors"
              >
                Book This Service <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-16 text-center p-8 rounded-sm bg-[#141414] border border-white/5 max-w-4xl mx-auto"
      >
        <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-3">Custom Transport &amp; Corporate Logistics</h3>
        <p className="text-white/70 font-light text-sm max-w-2xl mx-auto mb-6 leading-relaxed">
          Need tailored group logistics, executive drop-offs, or specialized travel routes across South Africa? Our team provides tailored solutions for every occasion.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link 
            to="/book" 
            className="px-6 py-3 bg-gold-base text-black font-bold uppercase tracking-widest text-xs hover:bg-gold-light transition-colors rounded-sm"
          >
            Book a Private Transfer
          </Link>
          <Link 
            to="/contact" 
            className="px-6 py-3 border border-white/20 text-white font-bold uppercase tracking-widest text-xs hover:bg-white/10 transition-colors rounded-sm"
          >
            Contact Skiva Africa Tours
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

